<template>
  <div class="form-list">
    <el-form
      class="form"
      ref="form"
      label-position="right"
      label-width="160px"
    >
      <div class="table-title table-title-top">课卡信息</div>

      <!-- 停卡中：顶部状态提示 + 停卡申请/记录跳转 -->
      <el-alert
        v-if="card.is_stopped && card.stop_info"
        class="stop-alert"
        type="warning"
        show-icon
        :closable="false"
        title="该课卡停卡中，停卡期间不可预约/上课"
      >
        <div class="stop-alert-line">{{ stopSummaryText }}</div>
        <div class="stop-alert-links">
          <router-link v-if="card.stop_info.update_apply_id" class="primary-color" :to="RouteConfig.cardUpdateApplyDetail.to(card.stop_info.update_apply_id)">查看停卡申请</router-link>
          <router-link v-if="card.stop_info.update_record_id" class="primary-color" :to="RouteConfig.cardUpdateRecordDetail.to(card.stop_info.update_record_id)">查看停卡记录</router-link>
        </div>
      </el-alert>

      <div class="field-group-label">办理信息</div>

      <el-form-item label="办理人">
        <template v-if="card.add_record">
          <v-avatar-nickname :value="card.add_record.applicant.avatar" :nickname="card.add_record.applicant.nickname" :is-center="false"/>
        </template>
        <span v-else class="info-color">未记录</span>
      </el-form-item>

      <el-form-item label="办卡门店">{{ card.store_name }}</el-form-item>

      <el-form-item label="办卡日期" prop="register_time">{{ card.register_time | timeToYmd }}</el-form-item>

      <el-form-item label="自动开卡日期" prop="start_time_auto">{{ card.start_time_auto | timeToYmdHi }}</el-form-item>

      <el-form-item v-if="isAdministrator" label="办理时间">
        <template v-if="card.add_record && card.add_record.add_time">
          {{ card.add_record.add_time | timeToYmdHi }}
        </template>
        <span v-else class="info-color">未记录</span>
      </el-form-item>

      <template v-if="card.add_record && card.add_record.type === 'apply'">
        <el-form-item v-if="canViewReviewInfo" label="审核人">
          <template v-if="card.add_record.reviewer">
            <v-avatar-nickname :value="card.add_record.reviewer.avatar" :nickname="card.add_record.reviewer.nickname" :is-center="false"/>
          </template>
          <span v-else class="info-color">未记录</span>
        </el-form-item>

        <el-form-item v-if="card.add_record.pass_remark && isAdministrator" label="审核备注">
          <span class="danger-color">{{ card.add_record.pass_remark }}</span>
        </el-form-item>
      </template>

      <div class="field-group-label">基础信息</div>

      <el-form-item label="所属学员">
        <v-avatar-nickname
          :value="student.selfie"
          :nickname="student.student_name"
          :is-center="false"
          :link-page-name="RouteConfig.studentDetail.name"
          :link-page-id="card.student_id"
          :avatar-size="50"
        />
      </el-form-item>

      <el-form-item label="课卡 ID">{{ id }}</el-form-item>

      <el-form-item label="课卡类型">{{ card.card_type_name }}</el-form-item>

      <el-form-item label="课卡名称">{{ card.card_name }}</el-form-item>

      <el-form-item label="是否初次办卡">
        <span v-if="card.is_new" class="success-color">是</span>
        <span v-else class="danger-color">否</span>
      </el-form-item>

      <el-form-item label="禁止预约专栏">
        <span v-if="card.is_disable_column" class="danger-color">是</span>
        <span v-else class="success-color">否</span>
      </el-form-item>

      <template v-if="card.is_stopped && card.stop_info">
        <div class="field-group-label">停卡信息</div>

        <el-form-item label="停卡状态">
          <span class="danger-color">停卡中</span>
        </el-form-item>

        <el-form-item label="停卡开始时间">{{ card.stop_info.stop_time | timeToYmdHi }}</el-form-item>

        <el-form-item label="已停时长">{{ stoppedDurationText }}</el-form-item>

        <el-form-item label="本次停卡天数">
          <span v-if="card.stop_info.is_unlimited_stop" class="warning-color">无固定期限（历史手动停卡，不自动开卡）</span>
          <span v-else>{{ card.stop_info.stop_days | toYuan }} 天</span>
        </el-form-item>

        <el-form-item label="预计自动开卡时间">
          <template v-if="card.stop_info.stop_expire_time">
            {{ card.stop_info.stop_expire_time | timeToYmdHi }}
            <span class="info-color">（剩余 {{ remainDurationText }}）</span>
          </template>
          <span v-else class="warning-color">不自动开卡，需人工提前开卡</span>
        </el-form-item>

        <el-form-item label="停卡发起方式">{{ card.stop_info.source_name }}</el-form-item>

        <el-form-item v-if="card.stop_info.administrator.nickname" :label="isStudentStop ? '审核人' : '操作人'">
          <v-avatar-nickname :value="card.stop_info.administrator.avatar" :nickname="card.stop_info.administrator.nickname" :is-center="false"/>
        </el-form-item>

        <el-form-item v-if="isStudentStop" label="申请人">
          <span class="success-color">学员本人</span>
        </el-form-item>

        <el-form-item v-if="card.stop_info.reason_type_name" label="停卡原因">{{ card.stop_info.reason_type_name }}</el-form-item>

        <el-form-item v-if="card.stop_info.reason_note" label="情况说明">{{ card.stop_info.reason_note }}</el-form-item>

        <el-form-item v-if="card.stop_info.review_time" label="审核时间">{{ card.stop_info.review_time | timeToYmdHi }}</el-form-item>

        <el-form-item v-if="card.stop_info.review_remark" label="审核意见">
          <span class="warning-color">{{ card.stop_info.review_remark }}</span>
        </el-form-item>

        <el-form-item v-if="(card.stop_info.remark_files || []).length" label="停卡凭证">
          <v-remark-file-list :files="card.stop_info.remark_files"/>
        </el-form-item>

        <el-form-item label="提前开卡后预计失效">
          <span v-if="card.stop_info.projected_expire_time" class="success-color">{{ card.stop_info.projected_expire_time | timeToYmdHi }}</span>
          <span v-else class="info-color">未开卡</span>
        </el-form-item>

        <el-form-item label="累计停卡次数">{{ card.stop_info.stop_count }} 次</el-form-item>
      </template>

      <div class="field-group-label">使用规则</div>

      <el-form-item label="适用门店">
        <template v-if="card.store_names && card.store_names.length">
          <span v-for="v in card.store_names" :key="v">{{ v }}</span>
        </template>
        <span v-else class="success-color">全国通用</span>
      </el-form-item>

      <template v-if="card.store_ids.length">
        <el-form-item label="适用门店限制天数" prop="store_limit_expire_days">
          {{ card.store_limit_expire_days }} 天
        </el-form-item>

        <el-form-item label="适用门店失效日期" prop="store_limit_expire_time">
          {{ card.store_limit_expire_time | timeToYmdHi }}
        </el-form-item>
      </template>

      <template v-if="cardTypeInfo.length">
        <el-form-item label="有效天数" prop="expire_days">
          {{ card.expire_days }} 天
        </el-form-item>

        <el-form-item label="剩余有效次数" prop="expire_number">
          <span v-if="card.is_number_limit">{{ card.expire_number | toYuan }} 次</span>
          <span v-else class="success-color">期限卡，无次数限制</span>
        </el-form-item>

        <el-form-item label="过期时间">
          <template v-if="card.expire_time">{{ card.expire_time | timeToYmdHi }}</template>
          <span v-else class="info-color">未开卡</span>
          <span v-if="card.is_stopped && card.stop_info && card.stop_info.projected_expire_time" class="info-color">（停卡前值，提前开卡后预计顺延至 {{ card.stop_info.projected_expire_time | timeToYmdHi }}）</span>
        </el-form-item>

        <el-form-item v-if="card.expire_time" label="开卡时间">{{ card.start_time | timeToYmdHi }}</el-form-item>
      </template>

      <el-form-item label="可用日期">
        <span v-if="allWeeksAvailable" class="success-color">无限制</span>
        <span v-else v-for="(v, k) in enabledWeeks" :key="v.value">
          {{ v.label }}{{ k === enabledWeeks.length - 1 ? '' : '、' }}
        </span>
      </el-form-item>

      <div class="field-group-label">支付信息</div>

      <el-form-item label="收款方式">
        <span v-if="card.actual_payment > 0">{{ findPaymentModeName(card.card_payment_mode_id) }}</span>
        <span v-else class="success-color">免费赠送</span>
      </el-form-item>

      <template v-if="card.actual_payment > 0 && card.card_payment_mode_id === paymentModes.youZan.value">
        <el-form-item label="有赞券码">{{ card.you_zan_number || '' }}</el-form-item>
        <el-form-item label="有赞核销金额">{{ card.you_zan_payment | toYuan }} 元</el-form-item>
      </template>

      <el-form-item label="积分抵扣">
        <span class="success-color">{{ card.integral | toYuan }}</span> 元
      </el-form-item>

      <el-form-item label="代金券抵扣">
        <template v-if="card.vouchers && card.vouchers.length">
          <div v-for="voucher in card.vouchers" :key="voucher.voucher_id" class="voucher-line">
            <router-link :to="RouteConfig.voucherDetail.to(voucher.voucher_id)" class="primary-color">{{ voucher.voucher_type_name }}</router-link>
            <span class="success-color"> {{ voucher.amount_in_cents | toYuan }} 元</span>
          </div>
          <div v-if="card.vouchers.length > 1" class="voucher-total">
            合计：<span class="success-color voucher-total-amount">{{ card.voucher_amount_in_cents | toYuan }}</span> 元
          </div>
        </template>
        <span v-else class="info-color">未使用代金券</span>
      </el-form-item>

      <el-form-item label="实际收款">
        <span class="income-color">{{ card.actual_payment | toYuan }}</span> 元
      </el-form-item>

      <el-form-item label="收款凭证">
        <template v-if="card.actual_payment > 0">
          <v-img v-if="card.receipt" :value="card.receipt" class="cover"/>
          <span v-else class="info-color">无</span>
        </template>
        <span v-else class="success-color">免费赠送</span>
      </el-form-item>

      <el-form-item label="付款备注">
        <span v-if="card.payment_remark" class="warning-color">{{ card.payment_remark }}</span>
        <span v-else class="info-color">无</span>
      </el-form-item>

      <el-form-item label="备注文件">
        <v-remark-file-list v-if="card.remark_files && card.remark_files.length" :files="card.remark_files"/>
        <span v-else class="info-color">未上传</span>
      </el-form-item>

      <el-form-item>
        <router-link v-if="card.card_del_apply_id" class="button-margin" :to="RouteConfig.cardDeleteApplyDetail.to(card.card_del_apply_id)">
          <el-button type="primary">退卡申请审核中，点击查看</el-button>
        </router-link>
        <router-link v-else-if="card.card_update_apply_id" class="button-margin" :to="RouteConfig.cardUpdateApplyDetail.to(card.card_update_apply_id)">
          <el-button type="primary">{{ card.card_update_apply_type === cardUpdateTypes.stop.value ? '停卡申请审核中，点击查看' : '课卡修改审核中，点击查看' }}</el-button>
        </router-link>
        <el-button v-else-if="card.is_reviewing" class="button-margin" :disabled="true" type="primary">修改申请审核中</el-button>
        <template v-else-if="card.is_stopped">
          <el-button v-if="isAdministrator" class="button-margin" type="warning" :loading="isUnstopping" @click="openUnstopDialog">提前开卡</el-button>
          <span v-else class="info-color button-margin">停卡中，需超管提前开卡后才能修改或退卡</span>
        </template>
        <template v-else>
          <router-link v-if="!isLoadFailed" :to="RouteConfig.cardEdit.to(id)" class="button-link">
            <el-button type="primary">修改课卡</el-button>
          </router-link>
          <router-link v-if="!isLoadFailed" :to="RouteConfig.cardDelete.to(id)" class="button-link">
            <el-button type="danger">退卡</el-button>
          </router-link>
        </template>
        <el-button @click="RouteConfig.cardList.back">返回</el-button>
      </el-form-item>
    </el-form>

    <!-- 提前开卡确认：讲清「什么时候停的 / 停了多久 / 失效日期从什么时候延到什么时候」 -->
    <el-dialog
      title="提前开卡"
      :visible.sync="isUnstopDialogVisible"
      width="480px"
      @closed="isUnstopDialogVisible = false"
    >
      <template v-if="card.stop_info">
        <div class="unstop-row">
          <span class="unstop-label">停卡开始时间</span>
          <span>{{ card.stop_info.stop_time | timeToYmdHi }}</span>
        </div>
        <div class="unstop-row">
          <span class="unstop-label">已停时长</span>
          <span class="danger-color">{{ stoppedDurationText }}</span>
        </div>
        <div class="unstop-row">
          <span class="unstop-label">本次停卡天数</span>
          <span v-if="card.stop_info.is_unlimited_stop">无固定期限（不自动开卡）</span>
          <span v-else>{{ card.stop_info.stop_days | toYuan }} 天</span>
        </div>
        <div class="unstop-row">
          <span class="unstop-label">失效日期</span>
          <template v-if="card.stop_info.projected_expire_time">
            <span class="info-color">{{ card.expire_time | timeToYmdHi }}</span>
            <v-icon name="arrow-right"/>
            <span class="success-color">{{ card.stop_info.projected_expire_time | timeToYmdHi }}</span>
          </template>
          <span v-else class="info-color">—</span>
        </div>
        <div class="unstop-row">
          <span class="unstop-label">自动开卡时间</span>
          <span v-if="card.stop_info.stop_expire_time" class="warning-color">{{ card.stop_info.stop_expire_time | timeToYmdHi }}（不操作则到期自动开卡）</span>
          <span v-else class="warning-color">不自动开卡，需人工提前开卡</span>
        </div>
        <el-alert class="unstop-tip" type="warning" show-icon :closable="false" :title="unstopTipText"/>
      </template>
      <div slot="footer">
        <el-button @click="isUnstopDialogVisible = false">取消</el-button>
        <el-button type="warning" :loading="isUnstopping" @click="confirmUnstop">确认提前开卡</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import RouteConfig from '@/router/config';
import weeks from "@/enums/weeks";
import { mapGetters } from 'vuex';
import { paymentModes } from "@/enums/paymentModes";
import { show, unstop } from '@/api/card';
import { findPaymentModeName } from "@/enums/paymentModes";
import cardUpdateTypes from '@/enums/cardUpdateTypes';
import { timeToYmdHi } from '@/filters';

export default {
  name: RouteConfig.cardEdit.name,
  data: () => ({
    RouteConfig,

    paymentModes,
    cardUpdateTypes,
    weeks: JSON.parse(JSON.stringify(weeks)),
    isLoadFailed: false,
    isSearching: false,
    isSaving: false,
    isUnstopping: false,
    isUnstopDialogVisible: false,
    cardTypeInfo: [],
    card: {
      add_record: null,
      add_time: 0,
      start_time: 0,
      student_id: 0,
      store_ids: [],
      is_generic: 0,
      card_type_id: 0,
      store_name: '',
      card_type_name: '',
      card_name: '',
      card_charge_type_id: '',
      vouchers: [],
      voucher_amount_in_cents: 0,
      integral: 0,
      is_number_limit: 0,
      expire_number: '',
      expire_days: '',
      expire_time: '',
      store_limit_expire_days: '',
      store_limit_expire_time: '',
      card_payment_mode_id: '',
      you_zan_number: '',
      actual_payment: '',
      you_zan_payment: '',
      payment_remark: '',
      receipt: '',
      remark_files: [],
      register_time: '',
      start_time_auto: '',
      update_remark: '',
      update_payment: '',
      is_reviewing: 0,
      is_stopped: 0,
      stop_time: 0,
      stop_expire_time: 0,
      stop_info: null,
      card_del_apply_id: 0,
      card_update_apply_id: 0,
      card_update_apply_type: 0,
      is_new: 0,
      is_disable_column: 0,
      disabled_weeks: [],
      store_names: '',
      card_payment_mode_name: '',
      store_id: 0,
    },
    student: {
      id: 0,
      student_name: '',
      nickname: '',
      selfie: '',
      phone: 0,
      card_number: '',
    },
  }),
  computed: {
    ...mapGetters(['isAdministrator', 'userStoreId']),
    canViewReviewInfo() {
      return this.isAdministrator || this.userStoreId === this.card.store_id;
    },
    isStudentStop() {
      return !!this.card.stop_info && this.card.stop_info.source !== 'admin_manual';
    },
    stoppedDurationText() {
      const info = this.card.stop_info;
      if (!info) return '-';
      return this.formatDuration(info.stopped_days, info.stopped_hours, info.stopped_minutes);
    },
    remainDurationText() {
      const info = this.card.stop_info;
      if (!info || !info.stop_expire_time) return '';
      return this.formatDuration(info.remain_days, info.remain_hours, info.remain_minutes);
    },
    // 提前开卡确认的说明：讲清顺延后果，以及「不操作则到期自动开卡」
    unstopTipText() {
      const info = this.card.stop_info;
      if (!info) return '';
      if (!info.stop_expire_time) return '该课卡为无固定期限停卡（不自动开卡），需提前开卡才能恢复使用。';
      return `提前开卡后课卡立即恢复使用，已停的 ${this.stoppedDurationText} 将顺延至失效日期；若不操作，系统将在 ${timeToYmdHi(info.stop_expire_time)} 自动开卡。`;
    },
    stopSummaryText() {
      const info = this.card.stop_info;
      if (!info) return '';
      const parts = [];
      if (this.card.stop_time) {
        parts.push(`停卡开始 ${timeToYmdHi(this.card.stop_time)}，已停 ${this.formatDuration(info.stopped_days, info.stopped_hours, info.stopped_minutes)}`);
      }
      if (info.is_unlimited_stop) {
        parts.push('无固定期限，不自动开卡');
      } else if (info.stop_expire_time) {
        parts.push(`${timeToYmdHi(info.stop_expire_time)} 自动开卡（剩余 ${this.formatDuration(info.remain_days, info.remain_hours, info.remain_minutes)}）`);
      }
      return parts.join('；');
    },
    allWeeksAvailable() {
      return !this.card.disabled_weeks || this.card.disabled_weeks.length === 0;
    },
    enabledWeeks() {
      return this.weeks.filter(w => w.checked);
    },
    id() {
      return +this.$route.params.id || 0;
    },
  },
  beforeMount() {
    this.show();
  },
  methods: {
    findPaymentModeName,
    // 时长展示：天+小时 / 小时+分钟 / 分钟（不足 1 分钟按 1 分钟，避免「不足 1 小时」这种别扭文案）
    formatDuration(days, hours, minutes = 0) {
      if (days > 0) return hours > 0 ? `${days} 天 ${hours} 小时` : `${days} 天`;
      if (hours > 0) return minutes > 0 ? `${hours} 小时 ${minutes} 分钟` : `${hours} 小时`;
      return `${Math.max(minutes, 1)} 分钟`;
    },
    // 提前开卡：先弹确认框展示停卡时间/已停时长/顺延后的失效日期，确认后再调接口
    openUnstopDialog() {
      if (this.isUnstopping || !this.card.is_stopped || !this.card.stop_info) return;
      this.isUnstopDialogVisible = true;
    },
    async confirmUnstop() {
      if (this.isUnstopping) return;

      this.isUnstopping = true;
      const { isError } = await unstop(this.id);
      this.isUnstopping = false;
      if (isError) return;

      this.isUnstopDialogVisible = false;
      this.$message.success('已提前开卡');
      this.show();
    },
    async show() {
      this.isLoadFailed = false;
      const { isError, data } = await show(this.id);
      if (isError || !data) return this.isLoadFailed = true;

      const { card } = this;
      const updateCardInfo = {};
      Object.keys(data).forEach(key => {
        if (key in card) updateCardInfo[key] = data[key];
      });
      Object.assign(card, updateCardInfo);

      if (data && Array.isArray(data.disabled_weeks) && data.disabled_weeks.length) {
        const { weeks } = this;
        for (let i = 0; i < weeks.length; i++) {
          weeks[i].checked = !data.disabled_weeks.includes(weeks[i].value);
        }
      }

      Object.assign(this, {
        cardTypeInfo: data.card_type_info || [],
        student: {
          id: data.student_id,
          student_name: data.student_name,
          nickname: data.nickname,
          selfie: data.selfie,
          phone: data.phone,
          card_number: data.card_number,
        },
      });
    },
  },
};
</script>
<style src="./card-detail.css" scoped/>
